/**
 * The batch ZIP job's contract — plan §2 U62, §8.2's *"batch generation (ZIP) is Starter
 * and above"*.
 *
 * `apps/api` evaluates §8.1's gates and writes a manifest; `apps/worker` renders from that
 * manifest and never re-evaluates a gate. The split is forced rather than chosen:
 * `.dependency-cruiser.cjs`'s `apps-never-import-apps` puts `evaluateGates` out of the
 * worker's reach, and a second gate walk in the worker is the *"two implementations that
 * agree today"* `ops/artwork.ts:317` already refuses.
 *
 * So a manifest is a **snapshot of the verdicts at enqueue**, and a product published or
 * archived between enqueue and render does not change what the ZIP holds. That is the
 * honest reading of a job: the rows the caller was shown in the same response are the rows
 * they get.
 */
import { z } from "zod";
import { ARTWORK_APPLICATION_PRESETS, ARTWORK_ECC_LEVELS, ARTWORK_FORMATS, ARTWORK_SYMBOLOGIES, } from "./api/artwork.js";
import { csvRow } from "./csv.js";
/**
 * The tier the batch ZIP is included from — §8.2: *"Free is single-symbol, one at a time,
 * within its 10-GTIN cap."*
 */
export const BATCH_ZIP_TIER = "starter";
/** The queue the API sends to. `drizzle/0033` creates it, by the owner. */
export const ARTWORK_BATCH_ZIP_QUEUE = "artwork.batch-zip";
/**
 * Set-equal to `artwork_batches.status`'s CHECK in `drizzle/0033`.
 *
 * `expired` is deliberately absent: expiry is a comparison against `expires_at` that any
 * reader can make, and a stored status would need a writer to keep it true.
 */
export const ARTWORK_BATCH_STATUSES = ["queued", "running", "ready", "failed"];
/**
 * How long a finished ZIP stays downloadable.
 *
 * Seven days, and no spec section states a number. A catalogue ZIP is re-generable from
 * the same door at any time, so the only thing a longer window buys is storage; a shorter
 * one loses a print buyer who comes back on Monday. Read by `GET /v1/artwork/batches/{id}`,
 * which withholds the presigned URL past it.
 */
export const ARTWORK_BATCH_TTL_HOURS = 168;
/** The report's name inside the ZIP. Present only when a row was withheld. */
export const ARTWORK_BATCH_REPORT_NAME = "report.csv";
/**
 * `artwork_batches.manifest` — the plan's *"requested rows"*, plus the render parameters
 * they are to be rendered with.
 *
 * One jsonb column rather than two, because neither half is meaningful alone: a verdict
 * without the format it was reached for is not a verdict §8.1 made.
 *
 * **The schema is the definition and the type is inferred from it**, which is the opposite
 * of how most contracts in this package are written. A `jsonb` column holds whatever was
 * put in it, and the process reading it is not the process that wrote it — so the parse is
 * the thing that must be right, and a hand-written type beside it would be a second
 * spelling that a cast could satisfy. {@link ArtworkBatchEntry} and
 * {@link ArtworkBatchWithheld} stay hand-written because `apps/api` builds them: assigning
 * one into this manifest is where the two spellings are checked against each other.
 */
export const artworkBatchManifestSchema = z.object({
    symbology: z.enum(ARTWORK_SYMBOLOGIES),
    format: z.enum(ARTWORK_FORMATS),
    xDimensionMm: z.number().positive().finite(),
    applicationPreset: z.enum(ARTWORK_APPLICATION_PRESETS),
    eccLevel: z.enum(ARTWORK_ECC_LEVELS).optional(),
    entries: z.array(z.object({
        productId: z.uuid(),
        gtin14: z.string(),
        digitalLinkUri: z.string(),
        unverifiedPrefix: z.boolean(),
    })),
    withheld: z.array(z.object({
        productId: z.string(),
        gtin14: z.string().nullable(),
        code: z.string(),
        title: z.string(),
        detail: z.string(),
    })),
});
/**
 * One entry's name inside the ZIP — the same filename `POST /v1/artwork` answers with.
 *
 * A GTIN is unique per organisation, so one batch cannot name two entries the same.
 */
export function batchEntryName(gtin14, symbology, format) {
    return `${gtin14}-${symbology}.${format}`;
}
/** The ZIP's own name, as the download is offered. */
export function batchZipName(batchId) {
    return `artwork-${batchId}.zip`;
}
/** The storage key segments, under the organisation's prefix. */
export function batchObjectSegments(batchId) {
    return ["artwork-batches", `${batchId}.zip`];
}
/**
 * §8.1's *"never failing whole"*, as a file — one row per withheld product, naming the
 * gate that withheld it.
 *
 * RFC 4180, UTF-8, CRLF, the same spelling `apps/api/src/evidence/csv.ts` writes, because
 * both reach {@link csvRow}.
 */
export const ARTWORK_BATCH_REPORT_COLUMNS = [
    "product_id",
    "gtin14",
    "code",
    "title",
    "detail",
];
export function batchReportCsv(withheld) {
    return [
        csvRow(ARTWORK_BATCH_REPORT_COLUMNS),
        ...withheld.map((row) => csvRow([row.productId, row.gtin14 ?? "", row.code, row.title, row.detail])),
    ]
        .map((line) => `${line}\r\n`)
        .join("");
}
//# sourceMappingURL=artwork-batch.js.map